import React, { useState } from "react";
import { authService } from "@/services/api/auth.service";
import { useAuth } from "../../../hooks/useAuth"; // Make sure the path matches your project structure
import { useNavigate } from "react-router-dom";
//import { useAuth } from "../../hooks/useAuth";

const ResetEmail = () => {
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleResetEmailLogout = () => {
    logout(); // Call the logout function from useAuth
    navigate("/Signin-Signup"); // Redirect to the login page (using your path)
  };
  const handleRequestEmailChange = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await authService.requestEmailChange(newEmail);

      if (response.success || response.status === "success") {
        setStep(2);
        setSuccess("Verification code sent to your current email address");
      } else {
        setError(response.message || "Failed to send verification code");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailChange = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await authService.verifyEmailChange({
        otp,
      });

      if (response.success || response.status === "success") {
        // Store the verification token for the final step
        if (response.data && response.data.verificationToken) {
          setVerificationToken(response.data.verificationToken);

          // Immediately complete the email change process
          await handleCompleteEmailChange(
            response.data.verificationToken,
            response.data.newEmail || newEmail
          );
        } else {
          setError(
            "Verification successful but missing token for email change"
          );
        }
      } else {
        setError(response.message || "Failed to verify email change");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // New function to handle the final step
  const handleCompleteEmailChange = async (token, email) => {
    setError("");
    setIsLoading(true);

    try {
      const completeResponse = await authService.completeEmailChange({
        verificationToken: token,
        newEmail: email,
      });

      if (completeResponse.success || completeResponse.status === "success") {
        setStep(3);
        setSuccess("Email changed successfully! Logging you out...");
        // Automatically logout after 2 seconds
        setTimeout(() => {
          logout();
          navigate("/Signin-Signup");
        }, 2000);
      } else {
        setError(completeResponse.message || "Failed to complete email change");
      }
    } catch (err) {
      setError(err.message || "Failed to complete email change");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* Step 1: Enter new email */}
      {step === 1 && (
        <form onSubmit={handleRequestEmailChange} className="space-y-4">
          <div>
            <label
              htmlFor="newEmail"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New Email Address
            </label>
            <input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your new email address"
            />
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? "Sending..." : "Send Verification Code"}
          </button>
        </form>
      )}

      {/* Step 2: Enter verification code */}
      {step === 2 && (
        <form onSubmit={handleVerifyEmailChange} className="space-y-4">
          {success && (
            <div className="text-green-500 text-sm mb-4">{success}</div>
          )}

          <div>
            <label
              htmlFor="otp"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Verification Code
            </label>
            <input
              id="otp"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter the 6-digit verification code"
            />
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? "Verifying..." : "Verify & Change Email"}
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Success confirmation */}
      {step === 3 && (
        <div className="text-center">
          <div className="mb-4 text-green-500">✓</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Email Changed Successfully
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            You will be automatically logged out...
          </p>
        </div>
      )}
    </div>
  );
};

export default ResetEmail;
